<template>
  <div v-if="isLoading" class="loading" />
  <RouterView v-else />
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import type { MenuItem } from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import {
  APP_SCREEN_TO_ORG_ROUTE,
  APP_SCREEN_TO_ROUTE,
  getAgentInfo,
  getAppScreenOrganizationId,
  getDatabaseInfo,
  getOrganizationHomeOrgId,
  isAgent,
  isDatabase,
  isOrganization,
} from "@/components/menu/menuItems";
import { waitForMenu } from "@/composables/useMenu";
import { RouteNames } from "@/routes/types";
import { getObjectRoutePath } from "@/utils/navigate";

export default defineComponent({
  setup() {
    const router = useRouter();
    const menu = inject.menu();
    const controller = inject.controller();
    const profile = inject.profile();
    const isLoading = ref(true);

    /**
     * Navigate to a menu item (app_screen or object)
     */
    const navigateToItem = async (item: MenuItem): Promise<void> => {
      if (item.target.kind === "app_screen") {
        const routeName = APP_SCREEN_TO_ROUTE[item.target.value];
        const orgRouteName = APP_SCREEN_TO_ORG_ROUTE[item.target.value];
        const orgId = getAppScreenOrganizationId(
          item,
          profile.value.user.personal.organizationId
        );
        if (orgRouteName && orgId) {
          await router.replace({
            name: orgRouteName,
            params: { orgId },
          });
        } else if (routeName) {
          await router.replace({ name: routeName });
        } else {
          await router.replace({ name: RouteNames.Agenda });
        }
      } else if (isOrganization(item)) {
        const orgId = getOrganizationHomeOrgId(item);
        if (orgId) {
          await router.replace({
            name: RouteNames.Organization,
            params: { orgId },
          });
        } else {
          await router.replace({ name: RouteNames.Agenda });
        }
      } else if (isDatabase(item)) {
        const info = getDatabaseInfo(item);
        if (info) {
          await router.replace({
            name: RouteNames.Database,
            params: {
              workspaceId: info.workspaceId,
              databaseId: info.tableId,
            },
          });
        } else {
          await router.replace({ name: RouteNames.Agenda });
        }
      } else if (isAgent(item)) {
        const info = getAgentInfo(item);
        if (info) {
          await router.replace({
            name: RouteNames.Agent,
            params: { agentId: info.agentId },
          });
        } else {
          await router.replace({ name: RouteNames.Agenda });
        }
      } else if (item.target.kind === "object") {
        const fullPath = await getObjectRoutePath(
          router,
          controller,
          item.target.value
        );
        await router.replace(fullPath);
      }
    };

    // If the user navigates away from the root path while the menu is still
    // loading, the resolved item must not hijack that navigation.
    let unmounted = false;
    onBeforeUnmount(() => {
      unmounted = true;
    });

    onMounted(async () => {
      const item = await waitForMenu(menu);
      if (unmounted) return;
      try {
        if (item) {
          await navigateToItem(item);
        } else {
          // Ultimate fallback
          await router.replace({ name: RouteNames.Agenda });
        }
      } finally {
        isLoading.value = false;
      }
    });

    return { isLoading };
  },
});
</script>

<style scoped lang="scss">
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
</style>
